import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  Map,
  Target,
  Briefcase,
  Users,
  Lightbulb,
  Award,
  MessageSquare,
  Route,
  FileText
} from 'lucide-react';
import { ROUTES } from '@constants';
import { PortfolioProvider, usePortfolio } from '@contexts/PortfolioContext';
import { ReportsProvider, useReports } from '@contexts/ReportsContext';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';

const DashboardContent = () => {
  const location = useLocation();
  const { reports, activeReport } = useReports();
  const { currentPlan } = usePlanRestrictions();
  const {
    careerStage,
    selectedCourse, 
    selectedSubjects,
    selectedState,
    selectedUniversities,
    opportunityType,
    selectedPersonalityTraits,
    selectedCareerDomains,
    selectedExams,
    selectedJobs,
    // Fresher-specific
    pursuingCourse,
    courseDuration,
    campusName,
    fresherAddress,
    fresherState,
    fresherDistrict,
    cgpaRange,
    selectedAptitudeTopics,
    selectedIndustryOutlook,
    resumeReady,
    // Professional-specific
    professionalTier,
    industryFocus,
    experienceLevel,
    careerSwitchPreferences,
    professionalSoftSkills,
    selectedHardSkills,
  } = usePortfolio();

  // Check if active report is locked (view-only)
  const isActiveReportLocked = activeReport && !activeReport.isEditable;

  const getCompletedSteps = () => {
    let completed = 0;
    let total = 0;
    
    if (careerStage) completed++;
    
    if (careerStage === 'fresher') {
      total = selectedJobs.length ? 9 : 7
      if (pursuingCourse && courseDuration && campusName) completed++;
      if (fresherAddress && fresherState && fresherDistrict) completed++;
      if (cgpaRange) completed++;
      if (selectedAptitudeTopics.length > 0) completed++;
      if (selectedExams.length > 0) completed++;
      if (selectedJobs.length > 0) completed++;
      if (selectedIndustryOutlook.length > 0) completed++;
      if (resumeReady) completed++;
    } else if (careerStage === 'professional') {
      total = professionalTier === 'Career Switch' ? 8 : 7;
      if (professionalTier) completed++;
      if (industryFocus) completed++;
      if (professionalTier === 'Career Switch' && careerSwitchPreferences.length > 0) completed++;
      if (experienceLevel) completed++;
      if (professionalSoftSkills.length > 0) completed++;
      if (selectedHardSkills.length > 0) completed++;
      if (selectedJobs.length > 0) completed++;
    } else if (careerStage === 'student') { 
      total = 9;
      if (selectedCourse) completed++;
      if (selectedSubjects.length > 0) completed++;
      if (selectedState) completed++;
      if (selectedUniversities.length > 0) completed++;
      if (opportunityType) completed++;
      if (selectedPersonalityTraits.length > 0) completed++;
      if (selectedCareerDomains.length > 0) completed++;
      if (selectedExams.length > 0) completed++;
      if (selectedJobs.length > 0) completed++;
    }
    
    return { completed, total };
  };

  const { completed, total } = getCompletedSteps();
  const isAllStagesCompleted = completed === total && careerStage;
  const hasReports = reports && reports.length > 0;

  const allTabs = [
    { name: 'Career Map',     path: ROUTES.STUDENT_CAREER_MAP,      icon: Map },
    { name: 'Career Roadmap', path: ROUTES.STUDENT_CAREER_ROADMAP,  icon: Route },
    { name: 'Education Portfolio', path: ROUTES.STUDENT_PORTFOLIO,   icon: Briefcase, requiresProfile: true },
    { name: 'Skill Tracker',  path: ROUTES.STUDENT_SKILL_TRACKER,   icon: Target, requiresProfile: true },
    { name: 'Opportunity',    path: ROUTES.STUDENT_OPPORTUNITY,      icon: Lightbulb, requiresProfile: true },
    { name: 'Career Journey Report',  path: ROUTES.STUDENT_CAREER_REPORT,   icon: FileText, hasNotification: isAllStagesCompleted, requiresReports: true },
    { name: 'Score Card',     path: ROUTES.STUDENT_SCORE_CARD,       icon: Award, requiresProPlan: true },
  ];

  // Filter tabs based on plan - Score Card only for Pro plan
  const tabs = allTabs.filter(tab => {
    if (tab.requiresProPlan) {
      return currentPlan?.name === 'Pro';
    }
    return true;
  });

  const isProfileSelected = !!careerStage;

  // Hide tabs on My Questions, Mentor, and Requests pages
  const hideTabsOnRoutes = [
    ROUTES.STUDENT_MY_QUESTIONS,
    ROUTES.STUDENT_MENTOR,
    ROUTES.STUDENT_REQUESTS
  ];
  const shouldShowTabs = !hideTabsOnRoutes.includes(location.pathname) && !location.pathname.startsWith('/student/mentor/');

  return (
    <div className="w-full min-w-0 max-w-full">
      {/* Tab Navigation */}
      {shouldShowTabs && (
        <div className="bg-gradient-to-br from-amber-50 to-secondary-50 rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden">
        {/*
          Key responsive strategy:
          - flex-wrap: lets tabs wrap onto a second (or third) row instead of overflowing
          - Each tab is flex-shrink-0 so it never shrinks/truncates its own content
          - On lg+ screens there are exactly 9 tabs that fit in one row
          - On smaller screens they naturally wrap into 2 rows
        */}
        <nav
          className="
            flex flex-wrap
            p-1.5 sm:p-2
            gap-1
          "
          aria-label="Dashboard navigation"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            // Disable tab if: profile not selected, no reports, OR report is locked (except Career Map and Career Journey Report)
            const isDisabled = (tab.requiresProfile && !isProfileSelected) || 
                               (tab.requiresReports && !hasReports) ||
                               (isActiveReportLocked && tab.path !== ROUTES.STUDENT_CAREER_REPORT && tab.path !== ROUTES.STUDENT_CAREER_MAP);

            const tabContent = (
              <>
                <Icon
                  className="
                    flex-shrink-0
                    w-3.5 h-3.5
                    sm:w-4 sm:h-4
                    lg:w-[18px] lg:h-[18px]
                  "
                />
                <span className="leading-none">
                  <span className="inline sm:hidden">{tab.name.split(' ')[0]}</span>
                  <span className="hidden sm:inline">{tab.name}</span>
                </span>
                {tab.hasNotification && (
                  <span className="relative flex-shrink-0 ml-0.5">
                    <span className="flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                    </span>
                  </span>
                )}
              </>
            );

            const baseClasses = `
              relative flex items-center justify-center gap-1.5
              flex-shrink-0
              px-3 sm:px-3.5 lg:px-4
              py-2 sm:py-2.5 lg:py-3
              text-[10px] sm:text-[11px] lg:text-sm
              font-semibold whitespace-nowrap
              rounded-md border border-transparent transition-colors duration-150
            `;

            if (isDisabled) {
              return (
                <div
                  key={tab.path}
                  className={`${baseClasses} group cursor-not-allowed text-gray-400 bg-gray-100/60 border-gray-200`}
                  aria-disabled="true"
                  title={isActiveReportLocked && tab.path !== ROUTES.STUDENT_CAREER_REPORT 
                    ? "Upgrade your plan to access this section" 
                    : "First select your Profile Type in Career Roadmap"}
                >
                  {tabContent}
                  {/* Hover tooltip */}
                  <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 hidden group-hover:flex items-center whitespace-nowrap rounded-md bg-gray-900 text-white text-[10px] sm:text-xs font-medium px-2.5 py-1.5 shadow-lg">
                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                    {isActiveReportLocked && tab.path !== ROUTES.STUDENT_CAREER_REPORT 
                      ? "Upgrade your plan to access" 
                      : "First select your Profile Type"}
                  </span>
                </div>
              );
            }

            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                end
                className={`${baseClasses} ${
                  isActive
                    ? 'bg-secondary-600 text-white border-secondary-600 shadow-[0_2px_8px_rgba(195,60,61,0.35)]'
                    : 'text-primary-600 hover:text-[#1a2642] hover:bg-white hover:border-[#1a2642]'
                }`}
                data-tour={tab.path === ROUTES.STUDENT_CAREER_MAP ? 'career-map-tab' : tab.path === ROUTES.STUDENT_CAREER_REPORT ? 'career-report' : undefined}
              >
                {tabContent}
              </NavLink>
            );
          })}
        </nav>
      </div>
      )}

      {/* Tab Content */}
      <div className="w-full min-w-0 max-w-full">
        <Outlet />
      </div>
    </div>
  );
};

const StudentDashboard = () => (
  <ReportsProvider>
    <PortfolioProvider>
      <DashboardContent />
    </PortfolioProvider>
  </ReportsProvider>
);

export default StudentDashboard;